import * as moment from "moment";
import React, { useRef } from "react";
import ReactToPrint from "react-to-print";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import PrintRow from "../PrintRow";
import { RootState } from "../../../../store";
import FooterQR from "../../../AssessmentQR/FooterQR";
import { t, tl } from "../../../../components/translate";
import { Allergy } from "../../../../interfaces/Allergy";
import { AssessmentClientInfo } from "../PrintComponents";
import { getClientById } from "../../../../actions/client";
import { TabularPrintContent } from "./TabularPrintContent";
import { getFullName } from "../../../../helpers/formatters";
import usePrintStyles from "../../../../hooks/usePrintStyles";
import CustomHeader from "../../../Lab/LabPrint/CustomHeader";
import { CustomEl } from "../../../Lab/LabEntry/LabTestSubRow";
import { getCustomerNumber } from "../../../Client/ClientList";
import { getAssessmentBaseUrl } from "../../../../helpers/urls";
import usePrintShortcut from "../../../../hooks/usePrintShortcut";
import useAssessmentLabel from "../../../../hooks/useAssessmentLabel";
import { Assessment } from "../../../../interfaces/AssessmentInterfaces";
import { spFullNameSelector } from "../../../../reducers/serviceProvider";
import { getSectionStyle } from "../../../Lab/LabPrint/LabPrintFunctions";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import InfoField from "../../../Lab/LabPrint/LabprintComponents/LabPrintInfofield";
import DoctorLetterHead from "../../../Lab/LabPrint/LabprintComponents/DoctorLetterHead";
import * as calendarFns from "../../../../components/Calendar/functions/calendarFunctions";
import LabPrintLetterHead from "../../../Lab/LabPrint/LabprintComponents/LabPrintLetterhead";
import LabPrintLetterHeadCentered from "../../../Lab/LabPrint/LabprintComponents/LabPrintLetterheadCentered";

// @ts-expect-error - image
import logo from "../../../../../assets/images/poweredByOkhati.png";
import { ServiceProvider } from "../../../../interfaces/ServiceProvidersInterface";

// eslint-disable-next-line import/prefer-default-export
export function AssessmentTabularPrint({
  buttonText = "Print Tabular Template",
  assessment,
  clientAllergies,
  isRiskInfoVisible,
  isCreationDateInADVisible,
  showAdAndBsAtOnce = false
}: {
  buttonText: string;
  assessment: Assessment;
  clientAllergies: Allergy[];
  isRiskInfoVisible: boolean;
  isCreationDateInADVisible: boolean;
  showAdAndBsAtOnce?: boolean;
}): React.ReactNode {
  const dispatch = useDispatch();
  const assessmentLabels = useAssessmentLabel();
  const resourceCentre = useCurrentResourceCentre();

  // selectors
  const serviceProviders = useSelector(
    (state: RootState) => state.resources.resourceCentreServiceProviders
  );
  const client = useSelector((state: RootState) =>
    state.clients.collection?.find(({ id }: { id: number }) => Number(assessment.clientId) === id)
  );

  // refs
  const componentRef = useRef(null);
  const reactToPrintRef = useRef(null);
  usePrintShortcut(reactToPrintRef);

  usePrintStyles({
    pageStyle: `
      size: A4;
      margin: 0mm;
      margin-bottom: 12pt;
      counter-increment: page;
      @bottom-center {
        content: "Page " counter(page) " of " counter(pages);
        font-size: 8pt;
      }`
  }).runPrintStyles();

  // variables
  const showLetterhead = Boolean(
    resourceCentre?.settings.printSettings.assessmentPrintSettings?.includeLetterhead
  );
  const { assessmentRemarks, showAssessmentRemarks } =
    resourceCentre.settings.printSettings.assessmentPrintSettings;

  const customHeaderImage = resourceCentre?.settings.assessmentSettings.customHeaderImage;
  const clinicNameFontScale: number = resourceCentre?.settings.printSettings.clinicNameFontScale;
  const centralizedHeader: boolean = resourceCentre?.settings?.printSettings.centralizedHeader;
  const headerColor: string =
    (resourceCentre?.settings.printSettings.labPrintSettings.enableColor &&
      resourceCentre?.settings.printSettings.labPrintSettings.color) ||
    "";
  const sortOrder = resourceCentre?.settings?.assessmentSettings?.sortOrder;
  const showComponentsInPrint = resourceCentre?.settings?.assessmentSettings?.showComponentsInPrint;

  const signers =
    assessment?.assessmentDocument?.authorizedSigners || assessment?.authorizedSigners;
  const signerSp1 = serviceProviders?.find(
    (sp: ServiceProvider) => sp.id === signers?.signerSp1?.id
  );
  const signerSp2 = serviceProviders?.find(
    (sp: ServiceProvider) => sp.id === signers?.signerSp2?.id
  );
  const signerSp3 = serviceProviders?.find(
    (sp: ServiceProvider) => sp.id === signers?.signerSp3?.id
  );

  const renderSigner = (signerSp: ServiceProvider) => {
    if (!signerSp) return null;
    return (
      <Box>
        {signerSp.signature ? (
          <img
            src={signerSp.signature}
            alt="Signer Signature"
            style={{ justifyContent: "flex-end", textAlign: "left" }}
            height="80"
            width="160"
          />
        ) : (
          <Box sx={{ height: 80, width: 160 }} />
        )}

        <Typography
          fontWeight="600"
          fontSize="0.775em"
          paddingTop="0.2cm"
          textAlign="left"
          borderTop="1px solid lightgrey"
        >
          {spFullNameSelector(signerSp)}
        </Typography>

        {signerSp.designation ? (
          <Typography fontSize="0.770em" style={{ whiteSpace: "pre-wrap" }}>
            {signerSp.designation}
          </Typography>
        ) : (
          <>
            <Typography fontSize="0.770em">{signerSp.qualification}</Typography>

            <Typography fontSize="0.770em">
              {signerSp.registrationNumber && signerSp.registrationNumber}
            </Typography>
          </>
        )}
      </Box>
    );
  };

  // helper functions
  const getLetterHead = () => {
    if (showLetterhead) {
      if (customHeaderImage) {
        return <CustomHeader imageUrl={customHeaderImage} />;
      }
      if (centralizedHeader) {
        return (
          <LabPrintLetterHeadCentered
            resourceCentre={resourceCentre}
            titleFontScale={clinicNameFontScale}
          />
        );
      }
      return (
        <LabPrintLetterHead resourceCentre={resourceCentre} titleFontScale={clinicNameFontScale} />
      );
    }
    return <div />;
  };

  const getDoctorLetterHead = () => {
    if (showLetterhead)
      return (
        <DoctorLetterHead
          serviceProvider={assessment.assessmentDocument.serviceProvider}
          titleFontScale={clinicNameFontScale}
          isCentered={centralizedHeader}
        />
      );
    return <div />;
  };

  // effects
  React.useEffect(() => {
    const clientId = assessment?.clientId;
    if (!client && clientId) {
      dispatch(getClientById(Number(clientId)));
    }
  }, [assessment?.clientId, dispatch, client]);

  return (
    <>
      <ReactToPrint
        onAfterPrint={() => {
          window.focus();
        }}
        trigger={() => (
          <Typography
            data-testmation="printButton"
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              fontSize: "0.95em",
              textTransform: "none",
              padding: "5px 15px"
            }}
            ref={reactToPrintRef}
          >
            {buttonText || "Print"}
          </Typography>
        )}
        content={() => componentRef.current}
      />

      <iframe
        style={{
          width: 0,
          height: 0,
          display: "contents"
        }}
        title="Assessment Print"
      >
        <div ref={componentRef}>
          {/* main content */}
          <Box component="table" width="100%">
            {/* table head */}
            <thead>
              {!showLetterhead && (
                <tr style={{ borderBottom: "1px solid lightgrey" }}>
                  <td>
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <div style={{ height: "3.5cm" }} />
                  </td>
                </tr>
              )}
              {showLetterhead && (
                <tr>
                  <td>
                    <Box style={{ ...getSectionStyle(headerColor).headerStyle }}>
                      {buttonText === "Print Doctor" ? getDoctorLetterHead() : getLetterHead()}
                    </Box>
                  </td>
                </tr>
              )}
            </thead>

            {/* table body */}
            <tbody>
              <tr>
                <td>
                  <Box
                    sx={{
                      ...getSectionStyle(headerColor).bodyStyle,
                      px: "10mm",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2
                    }}
                  >
                    {/* client info */}
                    <Box>
                      <Typography
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          fontSize: "0.5cm",
                          fontWeight: 600,
                          textTransform: "uppercase"
                        }}
                      >
                        {tl("patientReport")}
                      </Typography>

                      {client && (
                        <Box
                          sx={{
                            pb: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: "1px solid lightgray"
                          }}
                        >
                          <Box component="div" width="50%">
                            <AssessmentClientInfo
                              client={client}
                              customerNumber={getCustomerNumber(client?.customerNumber)}
                              hideRegistrationInfo
                            />
                          </Box>
                          <Box display="flex" flexDirection="column">
                            {client.registrationNo && (
                              <InfoField
                                label={t("assessment.registrationNumber")}
                                data={client.registrationNo}
                              />
                            )}
                            {client.ipdNo && <InfoField label={t("ipdNo")} data={client.ipdNo} />}
                            <InfoField
                              label="Created On"
                              data={(() => {
                                if (showAdAndBsAtOnce) {
                                  return (
                                    <Box>
                                      <p>
                                        {moment(assessment.createdDate).format("MMMM DD, YYYY")}
                                      </p>
                                      <p>
                                        {
                                          calendarFns.convertADtoBS(moment(assessment.createdDate))
                                            .formatted2
                                        }
                                      </p>
                                    </Box>
                                  );
                                }

                                return isCreationDateInADVisible
                                  ? moment(assessment.createdDate).format("MMMM DD, YYYY")
                                  : calendarFns.convertADtoBS(moment(assessment.createdDate))
                                      .formatted2;
                              })()}
                            />
                            {assessment?.visitType && (
                              <InfoField label="" data={assessment.visitType} highlight />
                            )}
                            {assessment?.client?.ssfId && (
                              <InfoField label="SSF Id" data={assessment.client.ssfId} />
                            )}
                            {assessment.claimCode && (
                              <InfoField label="Claim Code" data={assessment.claimCode} />
                            )}
                            {assessment?.referToSp && (
                              <InfoField
                                label="Refer To"
                                data={getFullName(assessment.referToSp)}
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* main data */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4mm"
                      }}
                    >
                      {isRiskInfoVisible && !!clientAllergies.length && (
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Typography>Risk Information:</Typography>
                          <Box>
                            {clientAllergies.map((item) => (
                              <Box key={item.id}>
                                <Typography fontWeight={600}>{item.name}</Typography>
                                {item.information && <Typography>{item.information}</Typography>}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {assessment && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            width: "100%"
                          }}
                        >
                          {sortOrder.map((componentName: string) => {
                            const shouldShow =
                              showComponentsInPrint?.[
                                componentName as keyof typeof showComponentsInPrint
                              ] ?? true;

                            if (!shouldShow) return null;

                            return (
                              <TabularPrintContent
                                key={componentName}
                                componentName={componentName}
                                assessment={assessment}
                                assessmentLabels={assessmentLabels}
                              />
                            );
                          })}

                          {assessment.violence && (
                            <PrintRow label={tl("assessment.violence")}>There is violence</PrintRow>
                          )}

                          {assessment.surgicalProcedure && (
                            <PrintRow label={tl("assessment.surgicalProcedure")}>
                              {assessment.surgicalProcedure}
                            </PrintRow>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* sp and signature */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end"
                      }}
                    >
                      {/* signer 1 */}
                      {renderSigner(signerSp1)}

                      {/* signer 2 */}
                      {renderSigner(signerSp2)}

                      {/* signer 3 */}
                      {renderSigner(signerSp3)}

                      {/* service provider */}
                      {assessment?.assessmentDocument &&
                        renderSigner(assessment.assessmentDocument.serviceProvider)}
                    </Box>

                    {/* assessment remarks */}
                    {showAssessmentRemarks && (
                      <Box px="10mm">
                        <Typography style={{ marginTop: "18px", padding: "0.2cm" }}>
                          <CustomEl data={assessmentRemarks} />
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </td>
              </tr>
            </tbody>

            {/* table footer */}
          </Box>

          {/* footer */}
          <Box component="div" width="100%" className="billFooterPos" marginBottom="-12pt">
            <Box
              paddingTop="0.5rem"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{
                ...getSectionStyle(headerColor).footerStyle
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                {assessment.assessmentUUID && (
                  <FooterQR url={`${getAssessmentBaseUrl()}/${assessment.assessmentUUID}`} />
                )}

                <img src={logo} alt="logo" height="35px" />
              </Box>
            </Box>
          </Box>
        </div>
      </iframe>
    </>
  );
}
